import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Card, Container, Row, Tab, Tabs,
} from 'react-bootstrap';
import { toast } from 'react-toastify';

import TerminalTable from '../components/tables/TerminalTable';
import TerminalForm from '../components/forms/TerminalForm';
import FormModal from '../components/modals/FormModal';
import ConfirmationModal from '../components/modals/ConfirmationModal';
import ToolbarContainer from './tables/ToolbarContainer';

import {
  addTerminal, loadTerminals, updateTerminal,
  activateTerminal, deactivateTerminal, decommissionTerminal,
} from '../actions/terminals';
import { updatePage, updatePageSize } from '../actions/pagination';
import loadLatestBptReport from '../actions/bptHealthReports';

import { Shapes } from '../models/entities';
import BptHealthReportForm from '../components/forms/reports/BptHealthReportForm';
import PrinterPaperReportForm from '../components/forms/reports/PrinterPaperReportForm';
import PersistentValuesReportForm from '../components/forms/reports/PersistentValuesReportForm';
import TerminalCredentialsModal from '../components/modals/TerminalCredentialsModal';
import TerminalToolbar from '../components/toolbars/TerminalToolbar';

class ManageTerminals extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      showModal: false,
      showReportModal: false,
      latestReport: null,
      modalTitle: '',
      modalError: undefined,
      selectedId: undefined,
      isEditing: true,
      showConfirm: false,
      confirmMessage: '',
      onConfirm: undefined,
      showCredentials: false,
      credentials: undefined,
    };
    this.onAdd = this.onAdd.bind(this);
    this.onEdit = this.onEdit.bind(this);
    this.onSave = this.onSave.bind(this);
    this.onActivate = this.onActivate.bind(this);
    this.onDeactivate = this.onDeactivate.bind(this);
    this.onDecommission = this.onDecommission.bind(this);
    this.onConfirmAction = this.onConfirmAction.bind(this);
    this.onDecommission = this.onDecommission.bind(this);
    this.onHideModal = this.onHideModal.bind(this);
    this.onHideConfirm = this.onHideConfirm.bind(this);
    this.onShowConfirm = this.onShowConfirm.bind(this);
    this.onHideCredentials = this.onHideCredentials.bind(this);
    this.onFetchLatestReport = this.onFetchLatestReport.bind(this);
  }

  componentDidMount() {
    const { loadTerminals: loadAllTerminals } = this.props;
    loadAllTerminals();
  }

  onAdd() {
    this.setState({
      showModal: true,
      modalTitle: 'Add New Terminal',
      isEditing: true,
    });
  }

  onEdit() {
    this.setState({
      showModal: true,
      modalTitle: 'Edit Terminal',
      isEditing: true,
    });
  }

  onSave(terminal) {
    const {
      addTerminal: onAdd, updateTerminal: onUpdate, loadTerminals: refreshTerminals,
    } = this.props;

    const { selectedId } = this.state;

    let promise;
    let action;
    if (selectedId) {
      promise = onUpdate({ ...terminal, id: selectedId });
      action = 'updated';
    } else {
      promise = onAdd(terminal).then((data) => {
        this.setState({
          showCredentials: true,
          credentials: data.response,
        });
      });
      action = 'created';
    }

    promise.then(() => refreshTerminals()).then(() => {
      this.onHideModal();
      toast.success(`Terminal ${terminal.name} ${action} successfully!`);
    }).catch((error) => this.setState({ modalError: error }));
  }

  onShowConfirm(action, onConfirm) {
    const { terminalsById } = this.props;
    const { selectedId } = this.state;
    this.setState({
      showConfirm: true,
      confirmMessage: `Are you sure you want to ${action} terminal '${terminalsById[selectedId].name}'?`,
      onConfirm,
    });
  }

  onActivate() {
    const { activateTerminal: onActivate } = this.props;
    this.onShowConfirm('activate', onActivate);
  }

  onDeactivate() {
    const { deactivateTerminal: onDeactivate } = this.props;
    this.onShowConfirm('deactivate', onDeactivate);
  }

  onDecommission() {
    const { decommissionTerminal: onDecommission } = this.props;
    this.onShowConfirm('decommission', onDecommission);
  }

  onConfirmAction() {
    const { selectedId, onConfirm } = this.state;
    const {
      loadTerminals: refreshTerminals, terminalsById,
    } = this.props;

    return onConfirm(selectedId).then(() => refreshTerminals())
      .then(() => toast.success(`Terminal ${terminalsById[selectedId].name} updated successfully!`))
      .catch((error) => toast.error(error.message))
      .finally(this.onHideConfirm);
  }

  onHideModal() {
    this.setState({
      showModal: false,
      showReportModal: false,
      latestReport: null,
      modalTitle: '',
      modalError: undefined,
      isEditing: false,
    });
  }

  onHideCredentials() {
    this.setState({
      showCredentials: false,
      credentials: undefined,
    });
  }

  onHideConfirm() {
    this.setState({
      showConfirm: false,
    });
  }

  onFetchLatestReport() {
    const { selectedId } = this.state;
    const { loadLatestBptReport: onLoadLatestReport } = this.props;

    onLoadLatestReport(selectedId).then((data) => {
      this.setState({
        showReportModal: true,
        latestReport: data.response,
      });
    }).catch((error) => toast.error(error.message));
  }

  render() {
    const {
      terminals, terminalsById, pagination, loadTerminals: refreshTerminals,
    } = this.props;

    const {
      showModal, modalError, modalTitle, showConfirm,
      confirmMessage, isEditing, selectedId, showReportModal,
      latestReport, showCredentials, credentials,
    } = this.state;

    const activate = selectedId && terminalsById[selectedId].status !== 'DECOMMISSIONED'
      && terminalsById[selectedId].systemStatus !== 'ACTIVE';

    const deactivate = selectedId && terminalsById[selectedId].status !== 'DECOMMISSIONED'
      && terminalsById[selectedId].systemStatus === 'ACTIVE';

    return (
      <Card style={{ height: '100%' }}>
        <Card.Header className="text-center">
          <strong style={{ fontSize: '22px' }}>Manage Terminals</strong>
        </Card.Header>
        <Card.Body>
          <Container>
            <Row>
              <ToolbarContainer
                pagination={pagination}
                onPaginationChange={refreshTerminals}
              >
                <TerminalToolbar
                  selectedId={selectedId}
                  onAdd={this.onAdd}
                  onDetails={this.onFetchLatestReport}
                  onEdit={this.onEdit}
                  onActivate={activate ? this.onActivate : undefined}
                  onDeactivate={deactivate ? this.onDeactivate : undefined}
                  onDecommission={selectedId && terminalsById[selectedId].status === 'DECOMMISSIONED' ? undefined : this.onDecommission}
                />
              </ToolbarContainer>
            </Row>
            <Row>
              <TerminalTable
                terminals={terminals}
                onSelectChange={(id) => this.setState({ selectedId: id })}
                selectedId={selectedId}
              />
              <FormModal
                show={showModal}
                title={modalTitle}
                isEditing={isEditing}
                onHide={this.onHideModal}
                formId="terminalForm"
                error={modalError}
              >
                <TerminalForm
                  terminal={selectedId ? terminalsById[selectedId] : null}
                  formId="terminalForm"
                  onSubmit={isEditing ? this.onSave : undefined}
                />
              </FormModal>
              <FormModal
                show={showReportModal}
                title="Latest Health Report"
                isEditing={false}
                onHide={this.onHideModal}
                formId="terminalReportForm"
              >
                <Tabs defaultActiveKey="reportTab" style={{ marginBottom: '1.5rem' }}>
                  <Tab eventKey="reportTab" title="Health Report">
                    <BptHealthReportForm
                      bptHealthReport={latestReport}
                    />
                  </Tab>
                  <Tab eventKey="printerReport" title="Printer Paper Report">
                    <PrinterPaperReportForm
                      bptHealthReport={latestReport}
                    />
                  </Tab>
                  <Tab eventKey="persistentReport" title="Persistent Values Report">
                    <PersistentValuesReportForm
                      bptHealthReport={latestReport}
                    />
                  </Tab>
                </Tabs>
              </FormModal>
              <ConfirmationModal
                title="Deactivate Terminal"
                message={confirmMessage}
                show={showConfirm}
                onConfirm={this.onConfirmAction}
                onClose={this.onHideConfirm}
              />
              <TerminalCredentialsModal
                show={showCredentials}
                terminal={credentials}
                onHide={this.onHideCredentials}
              />
            </Row>
          </Container>
        </Card.Body>
      </Card>
    );
  }
}

ManageTerminals.propTypes = {
  addTerminal: PropTypes.func.isRequired,
  updateTerminal: PropTypes.func.isRequired,
  activateTerminal: PropTypes.func.isRequired,
  deactivateTerminal: PropTypes.func.isRequired,
  decommissionTerminal: PropTypes.func.isRequired,
  loadTerminals: PropTypes.func.isRequired,
  loadLatestBptReport: PropTypes.func.isRequired,
  terminals: PropTypes.arrayOf(Shapes.TERMINAL),
  terminalsById: PropTypes.objectOf(PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    systemStatus: PropTypes.string,
    status: PropTypes.string,
  })),
  pagination: Shapes.PAGINATION,
};

ManageTerminals.defaultProps = {
  terminals: [],
  terminalsById: {},
  pagination: null,
};

const mapStateToProps = (state) => {
  const {
    pagination: { terminals: pagination },
    entities: { terminals },
  } = state;

  return {
    pagination,
    terminals: pagination.ids ? pagination.ids.map((id) => terminals[id]) : [],
    terminalsById: terminals,
  };
};

export default connect(mapStateToProps, {
  addTerminal,
  updateTerminal,
  activateTerminal,
  deactivateTerminal,
  decommissionTerminal,
  loadTerminals,
  updatePage,
  updatePageSize,
  loadLatestBptReport,
})(ManageTerminals);
